import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { useGetFansReleasesQuery, useGetFansSummaryQuery } from '../../../redux/api/fymAPI';
import { SMLRouteNames } from '../../../routing/sml/SMLRoutes';
import { PromoteContainer } from '../promote/PromoteContainer';
import { InsightsTabRow } from '../insights/InsightsTabRow';
import { insightsMessages } from '../insights/insightsMessages';
import { useInsightsCrumbs } from '../insights/useInsightsCrumbs';
import { smlNavigationMessages } from '../navigation/smlNavigationMessages';
import { FansEmpty } from './FansEmpty';
import { FansExportButton } from './FansExportButton';
import { FansGrowthPanel } from './FansGrowthPanel';
import { FansPeriodPicker } from './FansPeriodPicker';
import { FansPlanLockCard } from './FansPlanLockCard';
import { FansReleasesPanel } from './FansReleasesPanel';
import { FansServiceSplit } from './FansServiceSplit';
import { FansTotalsRow } from './FansTotalsRow';
import { useFansPeriod } from './useFansPeriod';
/* eslint-disable css-modules/no-unused-class -- one stylesheet per screen; each part of the screen uses its own slice of it */
import styles from './Fans.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FansScreenProps {
  accountId: number;
}

/**
 * Insights → Fans (design presave-subscriptions c1 to c6; head: navigation-v2 b 2d): the fans who
 * get every new release. The totals,
 * the growth chart, where they listen and the releases that went to them. With no subscribers and
 * no releases, the empty state replaces the panels (c4). On Free the plan card sits above the
 * content and the numbers stay (c6). The owner and a member see the same page.
 */
const FansScreenComponent: React.FC<FansScreenProps> = ({ accountId }) => {
  const intl = useIntl();
  const crumbs = useInsightsCrumbs(smlNavigationMessages.fans);
  const period = useFansPeriod();
  const { data: summary, isLoading, error, refetch } = useGetFansSummaryQuery({ accountId, days: period.days });
  // The first page of the list: the empty state needs to know that no release went out.
  const { data: firstReleases } = useGetFansReleasesQuery({ accountId, cursor: null });

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const isEmpty = summary !== undefined && summary.total === 0 && firstReleases?.releases.length === 0;

  const content = () => {
    if (isLoading) {
      return <LoadingView />;
    }
    if (error !== undefined || summary === undefined) {
      return <ErrorView error={error} retry={retry} />;
    }
    return (
      <>
        {summary.canUsePresave || isEmpty ? null : <FansPlanLockCard total={summary.total} />}
        {isEmpty ? (
          <FansEmpty accountId={accountId} canUsePresave={summary.canUsePresave} />
        ) : (
          <>
            <FansTotalsRow summary={summary} />
            <div className={styles.grid}>
              <FansGrowthPanel summary={summary} />
              <FansServiceSplit summary={summary} />
            </div>
            <FansReleasesPanel accountId={accountId} />
          </>
        )}
      </>
    );
  };

  return (
    <PromoteContainer
      title={intl.formatMessage(insightsMessages.title)}
      subtitle={intl.formatMessage(insightsMessages.subtitle)}
      crumbs={crumbs}
      tabs={<InsightsTabRow screen={SMLRouteNames.fans} />}
      actions={
        <div className={styles.headActions}>
          <FansPeriodPicker days={period.days} historyDaysLimit={period.historyDaysLimit} onChange={period.setDays} />
          <FansExportButton accountId={accountId} />
        </div>
      }
    >
      {content()}
    </PromoteContainer>
  );
};

export const FansScreen = TypedMemo(FansScreenComponent);
